import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import pb from '@/lib/pocketbase'
import type { Kegiatan, KegiatanTahap, KegiatanPanitia, KegiatanPeserta, KegiatanGaleri, KegiatanLaporan } from '@/types/pocketbase'
import { useAuthStore } from '@/stores/authStore'

const PAGE_SIZE = 20

async function fetchKegiatanList({
  page = 1,
  status,
  search,
  assignedOnly = false,
}: {
  page?: number
  status?: string
  search?: string
  assignedOnly?: boolean
}) {
  const filterParts: string[] = []
  if (status && status !== 'all') filterParts.push(`status = "${status}"`)
  if (search) filterParts.push(`(judul ~ "${search}" || tema ~ "${search}")`)

  const assignedIds = useAuthStore.getState().assignedKegiatanIds
  if (assignedOnly && assignedIds.length > 0) {
    const idsFilter = assignedIds.map((id) => `id = "${id}"`).join(' || ')
    filterParts.push(`(${idsFilter})`)
  }

  const filter = filterParts.length > 0 ? filterParts.join(' && ') : ''
  const result = await pb.collection('kegiatan').getList(page, PAGE_SIZE, {
    filter,
    sort: '-created',
    expand: 'created_by',
  })
  return result
}

async function fetchKegiatan(id: string) {
  const kegiatan = await pb.collection('kegiatan').getOne(id, {
    expand: 'created_by',
  })
  return kegiatan as Kegiatan
}

async function fetchTahapList(kegiatanId: string) {
  const result = await pb.collection('kegiatan_tahap').getFullList({
    filter: `kegiatan = "${kegiatanId}"`,
    sort: 'no_tahap',
  })
  return result as KegiatanTahap[]
}

async function fetchPanitiaList(kegiatanId: string) {
  const result = await pb.collection('kegiatan_panitia').getFullList({
    filter: `kegiatan = "${kegiatanId}"`,
    expand: 'user_id',
  })
  return result as KegiatanPanitia[]
}

async function fetchPesertaList(kegiatanId: string) {
  const result = await pb.collection('kegiatan_peserta').getFullList({
    filter: `kegiatan = "${kegiatanId}"`,
    expand: 'daerah_id,jabatan_id',
    sort: 'nama',
  })
  return result as KegiatanPeserta[]
}

async function fetchGaleriList(kegiatanId: string) {
  const result = await pb.collection('kegiatan_galeri').getFullList({
    filter: `kegiatan = "${kegiatanId}"`,
    expand: 'uploaded_by',
    sort: '-created',
  })
  return result as KegiatanGaleri[]
}

async function fetchLaporanList(params?: { kegiatan?: string; published?: boolean; jenis?: string }) {
  const filterParts: string[] = []
  if (params?.kegiatan) filterParts.push(`kegiatan = "${params.kegiatan}"`)
  if (params?.published !== undefined) filterParts.push(`published = ${params.published}`)
  if (params?.jenis) filterParts.push(`jenis = "${params.jenis}"`)
  const filter = filterParts.join(' && ')
  const result = await pb.collection('kegiatan_laporan').getList(1, 50, {
    filter,
    sort: '-created',
    expand: 'kegiatan,author',
  })
  return result
}

export function useKegiatanList(params: { status?: string; search?: string; assignedOnly?: boolean } = {}) {
  return useQuery({
    queryKey: ['kegiatan', 'list', params],
    queryFn: () => fetchKegiatanList(params),
  })
}

export function useKegiatan(id: string | undefined) {
  return useQuery({
    queryKey: ['kegiatan', id],
    queryFn: () => fetchKegiatan(id!),
    enabled: !!id,
  })
}

export function useTahapList(kegiatanId: string | undefined) {
  return useQuery({
    queryKey: ['kegiatan', kegiatanId, 'tahap'],
    queryFn: () => fetchTahapList(kegiatanId!),
    enabled: !!kegiatanId,
  })
}

export function usePanitiaList(kegiatanId: string | undefined) {
  return useQuery({
    queryKey: ['kegiatan', kegiatanId, 'panitia'],
    queryFn: () => fetchPanitiaList(kegiatanId!),
    enabled: !!kegiatanId,
  })
}

export function usePesertaList(kegiatanId: string | undefined) {
  return useQuery({
    queryKey: ['kegiatan', kegiatanId, 'peserta'],
    queryFn: () => fetchPesertaList(kegiatanId!),
    enabled: !!kegiatanId,
  })
}

export function useGaleriList(kegiatanId: string | undefined) {
  return useQuery({
    queryKey: ['kegiatan', kegiatanId, 'galeri'],
    queryFn: () => fetchGaleriList(kegiatanId!),
    enabled: !!kegiatanId,
  })
}

export function useLaporanList(params?: { kegiatan?: string; published?: boolean; jenis?: string }) {
  return useQuery({
    queryKey: ['laporan', params],
    queryFn: () => fetchLaporanList(params),
  })
}

export function useCreateKegiatan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Kegiatan>) => {
      const record = await pb.collection('kegiatan').create(data)
      return record
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kegiatan'] })
    },
  })
}

export function useUpdateKegiatan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Kegiatan> }) => {
      const record = await pb.collection('kegiatan').update(id, data)
      return record
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['kegiatan'] })
      qc.invalidateQueries({ queryKey: ['kegiatan', vars.id] })
    },
  })
}

export function useDeleteKegiatan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await pb.collection('kegiatan').delete(id)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kegiatan'] })
    },
  })
}

export function useAddPanitia() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<KegiatanPanitia>) => {
      const record = await pb.collection('kegiatan_panitia').create(data)
      return record
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['kegiatan', vars.kegiatan, 'panitia'] })
    },
  })
}

export function useRemovePanitia() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, kegiatanId }: { id: string; kegiatanId: string }) => {
      await pb.collection('kegiatan_panitia').delete(id)
      return kegiatanId
    },
    onSuccess: (kegiatanId) => {
      qc.invalidateQueries({ queryKey: ['kegiatan', kegiatanId, 'panitia'] })
    },
  })
}

export function useAddPeserta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<KegiatanPeserta>) => {
      const record = await pb.collection('kegiatan_peserta').create(data)
      return record
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['kegiatan', vars.kegiatan, 'peserta'] })
    },
  })
}

export function useUpdatePeserta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data, kegiatanId }: { id: string; data: Partial<KegiatanPeserta>; kegiatanId: string }) => {
      const record = await pb.collection('kegiatan_peserta').update(id, data)
      return { record, kegiatanId }
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['kegiatan', result.kegiatanId, 'peserta'] })
    },
  })
}

export function useRemovePeserta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, kegiatanId }: { id: string; kegiatanId: string }) => {
      await pb.collection('kegiatan_peserta').delete(id)
      return kegiatanId
    },
    onSuccess: (kegiatanId) => {
      qc.invalidateQueries({ queryKey: ['kegiatan', kegiatanId, 'peserta'] })
    },
  })
}

export function useAddGaleri() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: FormData) => {
      const record = await pb.collection('kegiatan_galeri').create(data)
      return record
    },
    onSuccess: (_, vars) => {
      const fd = vars as FormData
      const kegiatanId = fd.get('kegiatan') as string
      qc.invalidateQueries({ queryKey: ['kegiatan', kegiatanId, 'galeri'] })
    },
  })
}

export function useCreateLaporan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<KegiatanLaporan>) => {
      const record = await pb.collection('kegiatan_laporan').create(data)
      return record
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['laporan'] })
    },
  })
}

export function useUpdateLaporan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<KegiatanLaporan> }) => {
      const record = await pb.collection('kegiatan_laporan').update(id, data)
      return record
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['laporan'] })
    },
  })
}
